import CooperativeReturnsPage from './pages/staff/CooperativeReturnsPage';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Activity,
  Cable,
  CreditCard,
  DatabaseBackup,
  FileCheck2,
  GitBranch,
  Landmark,
  ListChecks,
  QrCode,
  ReceiptText,
  ScrollText,
  Settings,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import Layout from './components/layout/Layout';
import ProductAuthGate from './components/product/ProductAuthGate';
import ProductShellLayout from './components/product/ProductShellLayout';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './hooks/useAuth';
import { RequireAuth } from './components/auth/RequireAuth';
import { StaffSessionProvider } from './context/StaffSessionContext';
import WaiterAccessGuard from './pages/staff/WaiterAccessGuard';
import {
  MERCHANT_MOBILE_SWITCH_PATH,
  resolveMerchantLandingPath,
  resolveMerchantMobilePath,
  saveShellPreference,
} from './utils/accessPolicy';
import { t } from './i18n';

const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PublicContactCardPage = lazy(() => import('./pages/PublicContactCardPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const GuardedTaxPreviewPage = lazy(() => import('./pages/TaxPreviewPage'));

const AdminA3PayOperationsPage = lazy(() => import('./pages/admin/AdminA3PayOperationsPage'));
const A3PayOperationsWorkspace = lazy(async () => ({
  default: (await import('./pages/admin/AdminA3PayOperationsPage')).A3PayOperationsWorkspace,
}));
const AdminConnectedAccountsPage = lazy(() => import('./pages/admin/AdminConnectedAccountsPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminEventBuilderPage = lazy(() => import('./pages/admin/AdminEventBuilderPage'));
const AdminEventCatalogPage = lazy(() => import('./pages/admin/AdminEventCatalogPage'));
const AdminEventsPage = lazy(() => import('./pages/admin/AdminEventsPage'));
const AdminFinanceOperationsPage = lazy(() => import('./pages/admin/AdminFinanceOperationsPage'));
const AdminFiscalGatewayControlPlanePage = lazy(() => import('./pages/admin/AdminFiscalGatewayControlPlanePage'));
const AdminFiscalGatewayIntentsPage = lazy(() => import('./pages/admin/AdminFiscalGatewayIntentsPage'));
const FiscalGatewayIntentsWorkspace = lazy(async () => ({
  default: (await import('./pages/admin/AdminFiscalGatewayIntentsPage')).FiscalGatewayIntentsWorkspace,
}));
const AdminFiscalRegisterOperationsPage = lazy(() => import('./pages/admin/AdminFiscalRegisterOperationsPage'));
const FiscalRegisterOperationsWorkspace = lazy(async () => ({
  default: (await import('./pages/admin/AdminFiscalRegisterOperationsPage')).FiscalRegisterOperationsWorkspace,
}));
const AdminIntegrationLogsPage = lazy(() => import('./pages/admin/AdminIntegrationLogsPage'));
const AdminMerchantDetailPage = lazy(() => import('./pages/admin/AdminMerchantDetailPage'));
const AdminMerchantOverviewPage = lazy(() => import('./pages/admin/AdminMerchantOverviewPage'));
const AdminMerchantsListPage = lazy(() => import('./pages/admin/AdminMerchantsListPage'));
const AdminPartiesPage = lazy(() => import('./pages/admin/AdminPartiesPage'));
const AdminPartyDetailPage = lazy(() => import('./pages/admin/AdminPartyDetailPage'));
const AdminPermissionsPage = lazy(() => import('./pages/admin/AdminPermissionsPage'));
const AdminPosAlertsPage = lazy(() => import('./pages/admin/AdminPosAlertsPage'));
const AdminPosEventsPage = lazy(() => import('./pages/admin/AdminPosEventsPage'));
const AdminSchedulePlannerPage = lazy(() => import('./pages/admin/AdminSchedulePlannerPage'));
const AdminSplitRulesDesignerPage = lazy(() => import('./pages/admin/AdminSplitRulesDesignerPage'));
const AdminTaxPoliciesPage = lazy(() => import('./pages/admin/AdminTaxPoliciesPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminWaiterRecommendationKpiPage = lazy(() => import('./pages/admin/AdminWaiterRecommendationKpiPage'));
const ConnectedAccountOnboardingPage = lazy(() => import('./pages/admin/ConnectedAccountOnboardingPage'));

const GuestOrderPage = lazy(() => import('./pages/guest/GuestOrderPage'));
const GuestTermsPage = lazy(() => import('./pages/guest/GuestTermsPage'));
const ScheduledVisitAccessPage = lazy(() => import('./pages/guest/ScheduledVisitAccessPage'));
const ScheduledVisitWidgetPage = lazy(() => import('./pages/guest/ScheduledVisitWidgetPage'));

const MerchantDashboardPage = lazy(() => import('./pages/merchant/MerchantDashboardPage'));
const MerchantDishesWorkspacePage = lazy(() => import('./pages/merchant/MerchantDishesWorkspacePage'));
const MerchantEventsPage = lazy(() => import('./pages/merchant/MerchantEventsPage'));
const MerchantExternalPaymentsPage = lazy(() => import('./pages/merchant/MerchantExternalPaymentsPage'));
const MerchantFinanceOperationsPage = lazy(() => import('./pages/merchant/MerchantFinanceOperationsPage'));
const MerchantGoodsWorkspacePage = lazy(() => import('./pages/merchant/MerchantGoodsWorkspacePage'));
const BankConnectionsPage = lazy(() => import('./pages/admin/BankConnectionsPage'));
const MerchantAccountsPayablePage = lazy(() => import('./pages/merchant/MerchantAccountsPayablePage'));
const MerchantChecklistsWorkspacePage = lazy(() => import('./pages/merchant/MerchantChecklistsWorkspacePage'));
const MerchantOnboardingPage = lazy(() => import('./pages/merchant/MerchantOnboardingPage'));
const MerchantPortalPage = lazy(() => import('./pages/merchant/MerchantPortalPage'));
const MerchantStaffWorkspacePage = lazy(() => import('./pages/merchant/MerchantStaffWorkspacePage'));

const A3PayHostedPaymentStatusPage = lazy(() => import('./pages/product/A3PayHostedPaymentStatusPage'));
const A3PayMerchantSetupWorkspacePage = lazy(() => import('./pages/product/A3PayMerchantSetupWorkspacePage'));
const A3PayMerchantReadinessPage = lazy(() => import('./pages/product/A3PayMerchantReadinessPage'));
const A3PayPaymentAccountsPage = lazy(() => import('./pages/product/A3PayPaymentAccountsPage'));
const A3PayOperationsTracePage = lazy(() => import('./pages/product/A3PayOperationsTracePage'));
const A3PayProductionHardeningPage = lazy(() => import('./pages/product/A3PayProductionHardeningPage'));
const A3PayReceiptPreferencesPage = lazy(() => import('./pages/product/A3PayReceiptPreferencesPage'));
const A3PayReceiptTemplatesPage = lazy(() => import('./pages/product/A3PayReceiptTemplatesPage'));
const A3PayRegistryPage = lazy(() => import('./pages/product/A3PayRegistryPage'));
const FiscalGatewayBackfillPage = lazy(() => import('./pages/product/FiscalGatewayBackfillPage'));
const FiscalGatewayConnectorsPage = lazy(() => import('./pages/product/FiscalGatewayConnectorsPage'));
const FiscalGatewayCoopMirrorPage = lazy(() => import('./pages/product/FiscalGatewayCoopMirrorPage'));
const FiscalGatewayProductOverviewPage = lazy(() => import('./pages/product/FiscalGatewayProductOverviewPage'));
const FiscalGatewayPublicationsPage = lazy(() => import('./pages/product/FiscalGatewayPublicationsPage'));
const FiscalGatewayRolloutPage = lazy(() => import('./pages/product/FiscalGatewayRolloutPage'));
const FiscalGatewayRoutesPage = lazy(() => import('./pages/product/FiscalGatewayRoutesPage'));
const ProductLoginPage = lazy(() => import('./pages/product/ProductLoginPage'));
const TableServiceMerchantSetupPage = lazy(() => import('./pages/product/TableServiceMerchantSetupPage'));

const KitchenScreenPage = lazy(() => import('./pages/staff/KitchenScreenPage'));
const StaffOnboardingInvitePage = lazy(() => import('./pages/staff/StaffOnboardingInvitePage'));
const StaffChecklistPage = lazy(() => import('./pages/staff/StaffChecklistPage'));
const StaffPortalLayout = lazy(() => import('./pages/staff/StaffPortalLayout'));
const StaffPortalPage = lazy(() => import('./pages/staff/StaffPortalPage'));
const StaffStationPage = lazy(() => import('./pages/staff/StaffStationPage'));
const WaiterAcceptCashPage = lazy(() => import('./pages/staff/WaiterAcceptCashPage'));
const WaiterOrdersPage = lazy(() => import('./pages/staff/WaiterOrdersPage'));
const WaiterQrPage = lazy(() => import('./pages/staff/WaiterQrPage'));
const WaiterShellPage = lazy(() => import('./pages/staff/WaiterShellPage'));
const WaiterShiftClosePage = lazy(() => import('./pages/staff/WaiterShiftClosePage'));
const WaiterTableDetailPage = lazy(() => import('./pages/staff/WaiterTableDetailPage'));
const WaiterTablesPage = lazy(() => import('./pages/staff/WaiterTablesPage'));

const routeFallback = (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-600 dark:bg-slate-950 dark:text-slate-300">
    Loading...
  </div>
);

function MerchantEntryRedirect() {
  const { policy } = useAuth();
  return <Navigate to={resolveMerchantLandingPath(policy?.raw ?? null)} replace />;
}

function MerchantMobileRedirect() {
  const { policy } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    saveShellPreference('mobile');
    navigate(resolveMerchantMobilePath(policy?.raw ?? null), { replace: true });
  }, [navigate, policy]);

  return routeFallback;
}

function GuestRedirect({ to = '/guest' }: { to?: string }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

function TableServiceGuestRoute() {
  const [searchParams] = useSearchParams();
  const hasToken = ['token', 'guestToken', 'placeToken'].some((key) => {
    const value = searchParams.get(key);
    return Boolean(value?.trim());
  });
  return hasToken ? <GuestOrderPage /> : <Navigate to="/setup" replace />;
}

function A3TableServiceApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<GuestRedirect to="/setup" />} />
        <Route
          path="/login"
          element={<ProductLoginPage productName="A3 Table Service" entitlementName="Table Service" ProductIcon={QrCode} />}
        />
        <Route element={<TableServiceProductShell />}>
          <Route path="/setup" element={<TableServiceMerchantSetupPage />} />
          <Route path="/setup/*" element={<Navigate to="/setup" replace />} />
        </Route>
        <Route path="/guest" element={<TableServiceGuestRoute />} />
        <Route path="/table" element={<TableServiceGuestRoute />} />
        <Route path="/terms" element={<GuestTermsPage />} />
        <Route path="/scheduled-visit/access" element={<ScheduledVisitAccessPage />} />
        <Route path="/scheduled-visit/widget" element={<ScheduledVisitWidgetPage />} />
        <Route path="*" element={<GuestRedirect to="/setup" />} />
      </Routes>
    </AuthProvider>
  );
}

const tableServiceNavItems = [
  { label: 'Setup', to: '/setup', Icon: ListChecks, end: true },
];

function TableServiceProductShell() {
  const { logout, user } = useAuth();
  return (
    <ProductAuthGate>
      <ProductShellLayout
        productName="A3 Table Service"
        productTagline="QR ordering, menu, payment and receipt status"
        ProductIcon={QrCode}
        navItems={tableServiceNavItems}
        userLabel={user?.email ?? null}
        onLogout={logout}
      />
    </ProductAuthGate>
  );
}

const fiscalGatewayNavItems = () => [
  { label: t('fiscalGatewaySetup.nav.overview'), shortLabel: t('fiscalGatewaySetup.nav.shortOverview'), to: '/', Icon: Activity, end: true, mobile: true },
  { label: t('fiscalGatewaySetup.nav.integrations'), shortLabel: t('fiscalGatewaySetup.nav.shortConnection'), to: '/connectors', Icon: Cable, mobile: true },
  { label: t('fiscalGatewaySetup.nav.places'), shortLabel: t('fiscalGatewaySetup.nav.shortPlaces'), to: '/places', Icon: ListChecks },
  { label: t('fiscalGatewaySetup.nav.menuProducts'), shortLabel: t('fiscalGatewaySetup.nav.shortMenu'), to: '/menu-products', Icon: FileCheck2 },
  { label: t('fiscalGatewaySetup.nav.orderRoute'), shortLabel: t('fiscalGatewaySetup.nav.shortRoute'), to: '/order-route', Icon: GitBranch, mobile: true },
  { label: t('fiscalGatewaySetup.nav.paymentsReceipts'), shortLabel: t('fiscalGatewaySetup.nav.shortPayments'), to: '/payments-receipts', Icon: ReceiptText, mobile: true },
  { label: t('fiscalGatewaySetup.nav.health'), shortLabel: t('fiscalGatewaySetup.nav.shortHealth'), to: '/health', Icon: ShieldCheck, mobile: true },
  { label: t('fiscalGatewaySetup.nav.evidence'), shortLabel: t('fiscalGatewaySetup.nav.shortEvidence'), to: '/evidence', Icon: DatabaseBackup },
];

function FiscalGatewayProductShell() {
  const { logout, user } = useAuth();
  return (
    <ProductAuthGate>
      <ProductShellLayout
        productName={t('fiscalGatewaySetup.nav.productName')}
        productTagline={t('fiscalGatewaySetup.nav.productTagline')}
        ProductIcon={Cable}
        navItems={fiscalGatewayNavItems()}
        productConsoleLabel={t('fiscalGatewaySetup.header.productConsole')}
        readOnlyLabel={t('fiscalGatewaySetup.header.readOnly')}
        sectionsLabel={t('fiscalGatewaySetup.header.sections')}
        pinnedLabel={t('fiscalGatewaySetup.header.pinnedSections')}
        signOutLabel={t('fiscalGatewaySetup.header.signOut')}
        userLabel={user?.email ?? null}
        onLogout={logout}
      />
    </ProductAuthGate>
  );
}

function FiscalGatewayProductApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route
          path="/login"
          element={<ProductLoginPage productName="A3 Venue Bridge" entitlementName="Venue Bridge" ProductIcon={Cable} />}
        />
        <Route element={<FiscalGatewayProductShell />}>
          <Route index element={<FiscalGatewayProductOverviewPage />} />
          <Route path="connectors" element={<FiscalGatewayConnectorsPage />} />
          <Route path="places" element={<FiscalGatewayRoutesPage />} />
          <Route path="menu-products" element={<FiscalGatewayPublicationsPage />} />
          <Route path="order-route" element={<FiscalGatewayRoutesPage />} />
          <Route path="payments-receipts" element={<FiscalGatewayIntentsWorkspace />} />
          <Route path="health" element={<FiscalGatewayRolloutPage />} />
          <Route path="evidence" element={<FiscalGatewayCoopMirrorPage />} />
          <Route path="evidence/import" element={<FiscalGatewayBackfillPage />} />
          <Route path="intents" element={<Navigate to="/payments-receipts" replace />} />
          <Route path="routes" element={<Navigate to="/order-route" replace />} />
          <Route path="publications" element={<Navigate to="/menu-products" replace />} />
          <Route path="compatibility" element={<Navigate to="/evidence" replace />} />
          <Route path="data-import" element={<Navigate to="/evidence/import" replace />} />
          <Route path="coop-mirror" element={<Navigate to="/evidence" replace />} />
          <Route path="backfill" element={<Navigate to="/evidence/import" replace />} />
          <Route path="rollout" element={<Navigate to="/health" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

const fiscalRegisterNavItems = [
  { label: 'Operator', to: '/', Icon: UserRound, end: true },
  { label: 'Drafts', to: '/drafts', Icon: ListChecks },
  { label: 'Receipts', to: '/receipts', Icon: ReceiptText },
  { label: 'Reports', to: '/reports', Icon: FileCheck2 },
  { label: 'Journal', to: '/journal', Icon: ScrollText },
  { label: 'Runtime', to: '/runtime', Icon: Activity },
  { label: 'Settings', to: '/settings', Icon: Settings },
];

function FiscalRegisterProductShell() {
  const { logout, user } = useAuth();
  return (
    <ProductAuthGate>
      <ProductShellLayout
        productName="A3 Fiscal Register"
        productTagline="Receipts, reports, journal and register readiness"
        ProductIcon={ReceiptText}
        navItems={fiscalRegisterNavItems}
        userLabel={user?.email ?? null}
        onLogout={logout}
      />
    </ProductAuthGate>
  );
}

function FiscalRegisterProductApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route
          path="/login"
          element={<ProductLoginPage productName="A3 Fiscal Register" entitlementName="Fiscal Register" ProductIcon={ReceiptText} />}
        />
        <Route element={<FiscalRegisterProductShell />}>
          <Route index element={<FiscalRegisterOperationsWorkspace initialTab="operator" showTabs={false} />} />
          <Route path="drafts" element={<FiscalRegisterOperationsWorkspace initialTab="drafts" showTabs={false} />} />
          <Route path="receipts" element={<FiscalRegisterOperationsWorkspace initialTab="receipts" showTabs={false} />} />
          <Route path="reports" element={<FiscalRegisterOperationsWorkspace initialTab="reports" showTabs={false} />} />
          <Route path="journal" element={<FiscalRegisterOperationsWorkspace initialTab="journal" showTabs={false} />} />
          <Route path="runtime" element={<FiscalRegisterOperationsWorkspace initialTab="runtime" showTabs={false} />} />
          <Route path="settings" element={<FiscalRegisterOperationsWorkspace initialTab="settings" showTabs={false} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

const buildA3PayNavItems = () => [
  { label: t('a3PaySetup.nav.merchants'), to: '/merchants', Icon: Landmark, mobile: true },
  { label: t('a3PaySetup.nav.operations'), to: '/operations', Icon: GitBranch, mobile: true },
  { label: t('a3PaySetup.nav.readiness'), to: '/readiness', Icon: ShieldCheck, mobile: true },
  { label: t('a3PaySetup.nav.registry'), to: '/registry/merchants', Icon: Settings, mobile: true, showInPrimaryNav: false },
];

function A3PayProductShell() {
  const { logout, user } = useAuth();
  return (
    <ProductAuthGate>
      <ProductShellLayout
        productName="A3 Pay"
        productTagline={t('a3PaySetup.header.payTagline')}
        ProductIcon={CreditCard}
        navItems={buildA3PayNavItems()}
        userLabel={user?.email ?? null}
        onLogout={logout}
      />
    </ProductAuthGate>
  );
}

function A3PayProductApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route
          path="/login"
          element={<ProductLoginPage productName="A3 Pay" entitlementName="A3 Pay" ProductIcon={CreditCard} />}
        />
        <Route path="/status" element={<A3PayHostedPaymentStatusPage />} />
        <Route path="/status/:idOrKey" element={<A3PayHostedPaymentStatusPage />} />
        <Route path="/payment-requests/:idOrKey" element={<A3PayHostedPaymentStatusPage />} />
        <Route path="/receipt-preferences/:token" element={<A3PayReceiptPreferencesPage />} />
        <Route element={<A3PayProductShell />}>
          <Route index element={<Navigate to="/merchants" replace />} />
          <Route path="readiness" element={<A3PayProductionHardeningPage />} />
          <Route path="registry" element={<A3PayRegistryPage />} />
          <Route path="registry/:view" element={<A3PayRegistryPage />} />
          <Route path="merchants" element={<A3PayMerchantSetupWorkspacePage />} />
          <Route path="merchants/:merchantId/setup" element={<A3PayMerchantSetupWorkspacePage />} />
          <Route path="merchants/:merchantId/readiness" element={<A3PayMerchantReadinessPage />} />
          <Route path="operations" element={<A3PayOperationsWorkspace />} />
          <Route path="payment-acceptance" element={<A3PayPaymentAccountsPage />} />
          <Route path="payments" element={<A3PayOperationsTracePage />} />
          <Route path="settlement-remittance" element={<A3PayOperationsWorkspace initialTab="remittance" showTabs={false} />} />
          <Route path="risk-disputes" element={<A3PayOperationsWorkspace initialTab="risk" showTabs={false} />} />
          <Route path="safeguarding-reconciliation" element={<A3PayOperationsWorkspace initialTab="ledger" showTabs={false} />} />
          <Route path="receipt-emails" element={<A3PayReceiptTemplatesPage />} />
          <Route path="evidence" element={<A3PayProductionHardeningPage />} />
          <Route path="support-diagnostics" element={<A3PayOperationsWorkspace initialTab="acquirer" showTabs={false} />} />
          <Route path="trace" element={<Navigate to="/payments" replace />} />
          <Route path="receipts" element={<Navigate to="/receipt-emails" replace />} />
          <Route path="risk" element={<Navigate to="/risk-disputes" replace />} />
          <Route path="disputes" element={<Navigate to="/risk-disputes" replace />} />
          <Route path="safeguarding" element={<Navigate to="/safeguarding-reconciliation" replace />} />
          <Route path="remittance" element={<Navigate to="/settlement-remittance" replace />} />
          <Route path="reconciliation" element={<Navigate to="/safeguarding-reconciliation" replace />} />
          <Route path="acquirer" element={<Navigate to="/support-diagnostics" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

function App() {
  if (import.meta.env.VITE_PRODUCT_RUNTIME === 'pay') {
    return (
      <Suspense fallback={routeFallback}>
        <A3PayProductApp />
      </Suspense>
    );
  }

  if (import.meta.env.VITE_PRODUCT_RUNTIME === 'venue-bridge' || import.meta.env.VITE_PRODUCT_RUNTIME === 'fiscal-gateway') {
    return (
      <Suspense fallback={routeFallback}>
        <FiscalGatewayProductApp />
      </Suspense>
    );
  }

  if (import.meta.env.VITE_PRODUCT_RUNTIME === 'fiscal-register') {
    return (
      <Suspense fallback={routeFallback}>
        <FiscalRegisterProductApp />
      </Suspense>
    );
  }

  if (import.meta.env.VITE_PRODUCT_RUNTIME === 'table-service') {
    return (
      <Suspense fallback={routeFallback}>
        <A3TableServiceApp />
      </Suspense>
    );
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isGuestDomain = hostname === 'localhost' || hostname.includes('payattable');
  const defaultLanding = isGuestDomain ? '/guest' : '/staff/portal';

  return (
    <Suspense fallback={routeFallback}>
      <AuthProvider>
        <Routes>
        <Route path="/" element={<Navigate to={defaultLanding} replace />} />
        <Route path="/guest" element={<GuestOrderPage />} />
        <Route path="/terms" element={<GuestTermsPage />} />
        <Route path="/scheduled-visit/access" element={<ScheduledVisitAccessPage />} />
        <Route path="/scheduled-visit/widget" element={<ScheduledVisitWidgetPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/c" element={<PublicContactCardPage />} />
        <Route path="/c/:contactSlug" element={<PublicContactCardPage />} />
        <Route
          element={
            <StaffSessionProvider>
              <Outlet />
            </StaffSessionProvider>
          }
        >
          <Route path="/login" element={<LoginPage />} />
          <Route path="/station" element={<StaffStationPage />} />
          <Route path="/station/kitchen" element={<KitchenScreenPage />} />
          <Route path="/staff/portal/station" element={<StaffStationPage />} />
          <Route
            path="/staff/portal/*"
            element={
              <StaffPortalLayout />
            }
          >
            <Route index element={<StaffPortalPage />} />
            <Route path="checklist" element={<StaffChecklistPage />} />
            <Route path="kitchen" element={<KitchenScreenPage />} />
            <Route element={<WaiterAccessGuard />}>
              <Route path="waiter" element={<WaiterShellPage />} />
              <Route path="waiter/scan" element={<WaiterQrPage />} />
              <Route path="waiter/orders" element={<WaiterOrdersPage />} />
              <Route path="waiter/returns" element={<CooperativeReturnsPage />} />
              <Route path="waiter/tables" element={<WaiterTablesPage />} />
              <Route path="waiter/close-shift" element={<WaiterShiftClosePage />} />
              <Route path="waiter/tables/:posTableId" element={<WaiterTableDetailPage />} />
              <Route path="waiter/tables/:posTableId/accept-cash" element={<WaiterAcceptCashPage />} />
              {/* Legacy shortcuts without /waiter prefix */}
              <Route path="tables" element={<WaiterTablesPage />} />
              <Route path="close-shift" element={<WaiterShiftClosePage />} />
              <Route path="tables/:posTableId" element={<WaiterTableDetailPage />} />
              <Route path="tables/:posTableId/accept-cash" element={<WaiterAcceptCashPage />} />
            </Route>
          </Route>
        </Route>

        <Route
          path="/merchant"
          element={
            <RequireAuth>
              <MerchantEntryRedirect />
            </RequireAuth>
          }
        />
        <Route
          path={MERCHANT_MOBILE_SWITCH_PATH}
          element={
            <RequireAuth>
              <MerchantMobileRedirect />
            </RequireAuth>
          }
        />
        <Route
          path="/merchant/desktop"
          element={
            <RequireAuth>
              <MerchantDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/merchant/portal"
          element={
            <RequireAuth>
              <MerchantPortalPage />
            </RequireAuth>
          }
        />
        <Route
          path="/merchant/portal/staff"
          element={
            <RequireAuth>
              <MerchantStaffWorkspacePage />
            </RequireAuth>
          }
        />
        <Route
          path="/merchant/portal/checklists"
          element={
            <RequireAuth>
              <MerchantChecklistsWorkspacePage />
            </RequireAuth>
          }
        />
        <Route
          path="/merchant/portal/goods"
          element={
            <RequireAuth>
              <MerchantGoodsWorkspacePage />
            </RequireAuth>
          }
        />
        <Route path="/merchant/portal/bank-connections" element={<RequireAuth><BankConnectionsPage /></RequireAuth>} />
        <Route
          path="/merchant/portal/accounts-payable"
          element={
            <RequireAuth>
              <MerchantAccountsPayablePage />
            </RequireAuth>
          }
        />
        <Route
          path="/merchant/portal/dishes"
          element={
            <RequireAuth>
              <MerchantDishesWorkspacePage />
            </RequireAuth>
          }
        />

        <Route element={<Layout />}>
          <Route
            path="/merchant/onboarding"
            element={
              <RequireAuth>
                <MerchantOnboardingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/merchant/events"
            element={
              <RequireAuth>
                <MerchantEventsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/merchant/events/:eventId"
            element={
              <RequireAuth>
                <AdminEventBuilderPage />
              </RequireAuth>
            }
          />
          <Route
            path="/merchant/merchants/:merchantId"
            element={
              <RequireAuth>
                <AdminMerchantDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/merchant/merchants/:merchantId/schedule"
            element={
              <RequireAuth>
                <AdminSchedulePlannerPage />
              </RequireAuth>
            }
          />
          <Route
            path="/merchant/schedule"
            element={
              <RequireAuth>
                <AdminSchedulePlannerPage />
              </RequireAuth>
            }
          />
          <Route
            path="/merchant/external-payments"
            element={
              <RequireAuth>
                <MerchantExternalPaymentsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/merchant/finance/operations"
            element={
              <RequireAuth>
                <MerchantFinanceOperationsPage />
              </RequireAuth>
            }
          />
          <Route path="/merchant/reconciliation/split-payments" element={<Navigate to="/merchant/finance/operations?tab=reconciliation" replace />} />
          <Route path="/merchant/reconciliation/exports" element={<Navigate to="/merchant/finance/operations?tab=exports" replace />} />
          <Route path="/staff/onboarding/invite/:token" element={<StaffOnboardingInvitePage />} />
          <Route
            path="/admin"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminDashboardPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/parties"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminPartiesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/parties/:id"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminPartyDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/merchants/overview"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminMerchantOverviewPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/merchants"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminMerchantsListPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/merchants/:merchantId"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminMerchantDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/merchants/:merchantId/events"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminMerchantDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/pos/events"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminPosEventsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/pos/alerts"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminPosAlertsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/fiscal-gateway"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminFiscalGatewayControlPlanePage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/venue-bridge"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminFiscalGatewayControlPlanePage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/fiscal-gateway/intents"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminFiscalGatewayIntentsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/venue-bridge/payments-receipts"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminFiscalGatewayIntentsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/fiscal-register/operations"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminFiscalRegisterOperationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/a3-pay/operations"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminA3PayOperationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/integrations"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminIntegrationLogsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/analytics/waiter-recommendations"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminWaiterRecommendationKpiPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/schedule-planner"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminSchedulePlannerPage />
              </RequireAuth>
            }
          />
          <Route path="/admin/bank-connections" element={<RequireAuth><BankConnectionsPage /></RequireAuth>} />
          <Route
            path="/admin/finance/operations"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminFinanceOperationsPage />
              </RequireAuth>
            }
          />
          <Route path="/admin/reconciliation/split-payments" element={<Navigate to="/admin/finance/operations?tab=reconciliation" replace />} />
          <Route
            path="/admin/merchants/:merchantId/schedule"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminSchedulePlannerPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/permissions"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminPermissionsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']} requiredPerms={['platform:user:manage']}>
                <AdminUsersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']} requiredPerms={['platform:user:manage']}>
                <AdminUsersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/events"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminEventsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/connected-accounts"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminConnectedAccountsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/connected-accounts/:id/onboarding"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <ConnectedAccountOnboardingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/events/:eventId/catalog"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminEventCatalogPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/events/:eventId"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminEventBuilderPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/events/:eventId/rules"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminSplitRulesDesignerPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/tax-policies"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminTaxPoliciesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/tax-policies/new"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminTaxPoliciesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/tax-policies/:policyId"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminTaxPoliciesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/tax-policies/:policyId/edit"
            element={
              <RequireAuth requiredCapabilities={['manageEvents']}>
                <AdminTaxPoliciesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/tax/preview"
            element={
              <GuardedTaxPreviewPage />
            }
          />
        </Route>
        </Routes>
      </AuthProvider>
    </Suspense>
  );
}

export default App;
